import styled from '@emotion/styled';
import Cookies from 'js-cookie';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSignUpFormGlobalConfiguration } from '../../../../hook/use-sign-up-form-global-configuration';

import { ProductKeys } from '../../../../model';
import type { AnalyticsTrackType, Product, UserInfo } from '../../../../model';
import {
  buildSocialSignUpPayload,
  checkIfVisited,
  checkPerms,
  filterEligibleSites,
  getEncodedUserInfo,
  getProductKeys,
  getRenameSiteUrl,
  localStorageGet
} from '../../../../service';
import { SocialSignupErrorView } from './error';
import { messages } from './messages';

export type SocialSignUpViewProps = {
  readonly loadingImageUrl: string;
  readonly productKeys: Product[];
  readonly userInfo?: UserInfo;
  readonly isStaging: boolean;
  readonly wacEnvUrl: string;
  readonly devMode: boolean;
  readonly templateId?: string;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly setShowSignupForm: (val: boolean) => void;
  readonly tryAgainErrorUrl: string;
  readonly errorImageSrc: string;
  readonly testing?: boolean;
  readonly setShowSocialSignupForm?: (val: boolean) => void;
  readonly siteRenamePageUrl: string;
  readonly locale: string;
};

export const isProductIncluded = (productKeys: Product[], product: string) => {
  return _.includes(
    _.map(productKeys, (productKey: Product) => productKey.product),
    product
  );
};

export function SocialSignUpView(props: SocialSignUpViewProps) {
  const {
    userInfo,
    productKeys: productsSelectedForProvisioning,
    setShowSignupForm,
    loadingImageUrl,
    errorImageSrc,
    isStaging,
    wacEnvUrl,
    devMode,
    templateId,
    analyticsTrack,
    tryAgainErrorUrl,
    testing,
    setShowSocialSignupForm,
    siteRenamePageUrl
  } = props;
  const [showErrorPage, setShowErrorPage] = useState(false);
  const tryAgainUrl = `${tryAgainErrorUrl}/logout?continue=${window.location.href}`;

  const { confirmationPageUrl } = useSignUpFormGlobalConfiguration();

  useEffect(() => {
    // return if userInfo is not ready or the user is not logged in
    if (!userInfo || !userInfo.isLoggedIn) {
      analyticsTrack.segment('bxp.reactSocialSignup.notLoggedIn', {});
      return;
    }

    // check session storage if this user been on social signup before
    // redirect to stored rename url if they did
    if (checkIfVisited(userInfo, setShowSignupForm)) {
      analyticsTrack.segment('bxp.reactSocialSignup.alreadyVisited', {});
      return;
    }

    const productKeysSelectedForProvisioning = getProductKeys(
      productsSelectedForProvisioning
    );

    const isJsw = isProductIncluded(
      productsSelectedForProvisioning,
      ProductKeys.JIRA_SOFTWARE
    );
    const isJsm = isProductIncluded(
      productsSelectedForProvisioning,
      ProductKeys.JIRA_SERVICE_DESK
    );
    const isJwm = isProductIncluded(
      productsSelectedForProvisioning,
      ProductKeys.JIRA_CORE
    );
    // suppress User Segmentation Screen for Jira Template Signups
    const hideUserSegmentation = !!(templateId && (isJsw || isJsm || isJwm));

    // show the N2E signup form if the user has existing sites that do not
    // already have the selected products or do but they are extended trials
    const eligibleSites = filterEligibleSites(
      userInfo,
      productKeysSelectedForProvisioning
    );

    const hasEligibleSites = eligibleSites.length > 0;
    // has eligible sites so show N2E form for user to select existing site
    // to add these new products to
    setShowSignupForm(hasEligibleSites);
    analyticsTrack.segment('bxp.reactSocialSignup.hasEligibleSites', {
      hasEligibleSites
    });

    if (hasEligibleSites) {
      // setShowSignupForm equaling false will make the signupform render,
      // if its true the loading gif will always show
      if (setShowSocialSignupForm !== undefined) {
        setShowSocialSignupForm(false);
      }
      analyticsTrack.segment('bxp.reactSocialSignup.foundEligibleSites', {
        numEligibleSites: eligibleSites.length,
        productKeysSelectedForProvisioning
      });
      analyticsTrack.newRelic.action(
        'Found Eligible Sites',
        JSON.stringify({
          numEligibleSites: eligibleSites.length,
          productKeysSelectedForProvisioning
        })
      );
    } else {
      if (!!userInfo.sites && userInfo.sites?.length > 1) {
        analyticsTrack.segment('bxp.reactSocialSignup.foundExistingSites', {
          numExistingSites: userInfo.sites.length,
          productKeysSelectedForProvisioning
        });
        analyticsTrack.newRelic.action(
          'Found Existing Sites',
          JSON.stringify({
            numExistingSites: userInfo.sites.length,
            productKeysSelectedForProvisioning
          })
        );
      } else {
        analyticsTrack.segment('bxp.reactSocialSignup.foundNoSites', {});
        analyticsTrack.newRelic.action('Found No Sites', '');
      }

      // if no eligible sites, create new site and redirect to confirmation page
      const payload = buildSocialSignUpPayload(
        userInfo,
        productsSelectedForProvisioning,
        templateId,
        devMode,
        isStaging,
        hideUserSegmentation
      );

      const submitUrl = userInfo.isLoggedIn
        ? `https://${
            isStaging ? 'wac.stg.internal.' : 'www.'
          }atlassian.com/gateway/api/cloud/signup`
        : `${wacEnvUrl}/endpoint/cloud/signupAnon`;
      fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'atl-cookies': JSON.stringify({
            _ga: Cookies.get('_ga') || '',
            seg_xid: localStorageGet('seg_xid'),
            __atl_path: Cookies.get('__atl_path') || '',
            ajs_anonymous_id: Cookies.get('ajs_anonymous_id') || ''
          })
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      })
        .then((response: Response) => {
          if (response.ok && response.status === 200) {
            response
              .json()
              .then((body) => {
                analyticsTrack.segment(
                  'bxp.cloud-signup.social-signup-page.site-created',
                  {
                    cloudId: body.cloudId,
                    status: 200
                  }
                );
                const productKeysToAdd = productsSelectedForProvisioning.map(
                  ({ product: newProductKeyToAdd }) =>
                    newProductKeyToAdd as string
                );
                let isPermitted = false;
                checkPerms(body.cloudId, wacEnvUrl, analyticsTrack)
                  .then((res) => {
                    isPermitted = res.permitted;
                    analyticsTrack.segment(
                      'bxp.cloud-signup.rename.site.completed.permissions.check',
                      {
                        cloudId: body.cloudId,
                        status: 200,
                        permitted: res.permitted
                      }
                    );
                    analyticsTrack.newRelic.action(
                      'bxp.cloud-signup.rename.site.completed.permissions.check',
                      JSON.stringify({
                        cloudId: body.cloudId,
                        status: 200,
                        permitted: res.permitted
                      })
                    );
                    const data = {
                      category: 'cart-cloud',
                      action: 'Completed permissions check',
                      label: 'complete-social-signup-page',
                      attributes: {
                        cloudId: body.cloudId,
                        status: 200,
                        permitted: res.permitted
                      },
                      isNonInteraction: true
                    };
                    analyticsTrack.segment(data.label, data);
                    const renameUrl = getRenameSiteUrl({
                      wacEnvUrl,
                      confirmationPageUrl,
                      cloudId: body.cloudId,
                      cloudName: body.cloudName,
                      productKeys: productKeysToAdd,
                      progressUri: body.progressUri,
                      isPermitted,
                      siteRenamePageUrl,
                      hideUserSegmentation
                    });

                    sessionStorage.setItem(
                      'social-signup',
                      window.btoa(
                        JSON.stringify({
                          userInfo: getEncodedUserInfo(userInfo),
                          redirectUrl: renameUrl
                        })
                      )
                    );
                    window.location.replace(renameUrl);
                  })
                  .catch((e) => {
                    isPermitted = false;
                    analyticsTrack.segment(
                      'bxp.cloud-signup.rename.site.failed.permissions.check',
                      {
                        cloudId: body.cloudId,
                        message: e.message
                      }
                    );
                    analyticsTrack.newRelic.action(
                      'bxp.cloud-signup.rename.site.failed.permissions.check',
                      JSON.stringify({
                        cloudId: body.cloudId,
                        message: e.message
                      })
                    );
                    const data = {
                      category: 'cart-cloud',
                      action: 'Failed permissions check',
                      label: 'complete-social-signup-page',
                      attributes: {
                        cloudId: body.cloudId,
                        message: e.message
                      },
                      isNonInteraction: true
                    };
                    analyticsTrack.segment(data.label, data);
                    const renameUrl = getRenameSiteUrl({
                      wacEnvUrl,
                      confirmationPageUrl,
                      cloudId: body.cloudId,
                      cloudName: body.cloudName,
                      productKeys: productKeysToAdd,
                      progressUri: body.progressUri,
                      isPermitted,
                      siteRenamePageUrl,
                      hideUserSegmentation
                    });
                    sessionStorage.setItem(
                      'social-signup',
                      window.btoa(
                        JSON.stringify({
                          userInfo: getEncodedUserInfo(userInfo),
                          redirectUrl: renameUrl
                        })
                      )
                    );
                    window.location.replace(renameUrl);
                  });
              })
              .catch(() => {
                // To-Do show error screen
                setShowErrorPage(true);
              });
          } else {
            // To-Do show error screen
            setShowErrorPage(true);
          }
        })
        .catch(() => {
          // To-Do show error screen
          setShowErrorPage(true);
        });
    }
  }, [userInfo]);

  return (
    <Root>
      {!(showErrorPage || testing) && (
        <>
          <img src={loadingImageUrl} alt={'loading...'} width={600} />
          <FormattedMessage {...messages.socialSignUpLoadingText} />
        </>
      )}
      {(showErrorPage || testing) && (
        <SocialSignupErrorView
          tryAgainUrl={tryAgainUrl}
          errorImageSrc={errorImageSrc}
          wacEnvUrl={wacEnvUrl}
        />
      )}
    </Root>
  );
}

SocialSignUpView.messages = messages;

export default injectIntl(SocialSignUpView);

const Root = styled.div`
  margin: 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
`;
